import { useMemo, useState } from 'react';
import { Program } from '@src/graphql/home/queries';
import { useEnrollmentsData } from '@src/context/EnrollmentsContext';
import { UserEnrollment, EnrollmentIntentions } from '@src/api/enrollmentServiceClient';
import { IMemberUserQuery } from '@src/generated/gateway/graphql';
import useGetProgramsByUuid from './useGetProgramsByUuid';
import useGetMemberUser from './useGetMemberUser';

export enum ApplicationState {
  STARTED = 'overview_completed',
  APPROVED = 'approved',
  FINISHED = 'finished',
  DEFERRED = 'deferred',
  NEW = 'new',
}

export type Application = IncompleteApplication & {
  program: Program;
};

export interface IncompleteApplication {
  id: string;
  uuid: string;
  state?: string | null;
  programUuid?: string | null;
  academicPartnerUuid?: string | null;
  enrollmentIntentions?: null | EnrollmentIntentions[];
}

/**
 * Inserts program data into applications and removes applications without programIds
 * @param applications IncompleteApplication
 * @param programs Program
 * @returns Application[]
 */
export const filterByAndInsertPrograms = (
  applications: IncompleteApplication[],
  programs: { key: string; value: Program } | {}
) => {
  return applications.reduce((acc: Application[], incompleteApplication) => {
    const program = incompleteApplication.programUuid
      ? programs[incompleteApplication.programUuid]
      : null;
    if (!program) return acc;
    const application: Application = {
      ...incompleteApplication,
      program,
    };
    acc.push(application);
    return acc;
  }, []);
};

/**
 * Inserts enrollments data into applications
 * @param applications Application
 * @param enrollments Enrollments
 * @returns Application[]
 */
export const insertEnrollments = (
  applications: Application[],
  enrollments: { key: string; value: UserEnrollment } | {}
) => {
  return applications.map((application) => {
    const enrollment = enrollments[application.uuid];
    return { ...application, enrollmentIntentions: enrollment?.enrollmentHistory || null };
  });
};

export const formatApplications = (applications: Application[]) => {
  const completedApplications: Application[] = [];
  const approvedApplications: Application[] = [];
  const applicationsToFinish: Application[] = [];
  for (let i = 0; i < applications.length; i++) {
    const application = applications[i];

    switch (application.state) {
      case ApplicationState.APPROVED:
        approvedApplications.push(application);
        break;
      case ApplicationState.FINISHED:
        completedApplications.push(application);
        break;
      case ApplicationState.DEFERRED:
        completedApplications.push(application);
        break;
      case ApplicationState.STARTED:
        applicationsToFinish.push(application);
        break;
      default:
        applicationsToFinish.push(application);
    }
  }

  return {
    completedApplications,
    approvedApplications,
    applicationsToFinish,
  };
};

const mapDependencies = (key: string, dependencies?: Array<any>) => {
  if (!dependencies) return {};
  return dependencies.reduce((acc, element) => {
    if (element?.[key]) acc[element[key]] = element;
    return acc;
  }, {});
};

/**
 * @name useApplications
 * @description A hook that gets a user's applications.
 * @example
 * const {
 *   loading,
 *   error,
 *   completedApplications,
 *   approvedApplications,
 *   applicationsToFinish
 * } = useApplications();
 * @returns An object containing loading, error, and application data.
 */
const useApplications = () => {
  const [programUuids, setProgramUuids] = useState<string[]>([]);
  const { enrollments, loading: loadingEnrollments } = useEnrollmentsData();

  const onCompleted = (data: IMemberUserQuery) => {
    const programUuids = (data?.memberUser?.applications || []).reduce(
      (acc: string[], application) => {
        if (application.programUuid) acc.push(application.programUuid);
        return acc;
      },
      []
    );
    setProgramUuids(programUuids);
  };
  const { loading: loadingMemberUser, error, memberUser } = useGetMemberUser(onCompleted);

  const { programData, loading: loadingPrograms } = useGetProgramsByUuid(programUuids);

  const programMap = useMemo(() => mapDependencies('uuid', programData), [programData]);

  const enrollmentMap = useMemo(() => mapDependencies('applicationUuid', enrollments), [
    enrollments,
  ]);

  const applicationsWithPrograms = filterByAndInsertPrograms(
    memberUser?.applications || [],
    programMap
  );
  const applicationsWithEnrollments = insertEnrollments(applicationsWithPrograms, enrollmentMap);
  const formattedApplications = formatApplications(applicationsWithEnrollments);

  return {
    loading: loadingEnrollments || loadingMemberUser || loadingPrograms,
    error,
    ...formattedApplications,
  };
};

export default useApplications;
